import {get, patch, post} from './http';

export type QcReviewStatus = 'pending' | 'approved' | 'rejected';

export type QcPolicyViolationQueueSummary = {
  policy_violation: string;
  pending: number;
  pending_content: number;
  pending_actor: number;
  approved: number;
  rejected: number;
};

export type QcReviewItem = {
  id: string;
  submission_id: string;
  classification_id: string | null;
  analyst_email: string;
  policy_violation: string;
  original_risk: string;
  original_notes: string | null;
  status: QcReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  was_edited: boolean;
  edited_policy_violation: string | null;
  edited_risk: string | null;
  review_notes: string | null;
  created_at: string;
  item_external_id: string | null;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  platform: string;
  author_handle: string | null;
  author_name: string | null;
  published_at: string | null;
  metadata: Record<string, unknown>;
  current_delivery_state: string;
  // The submission's current analyst-authored attributes (after QC edits, once approved) - what the
  // "approve with edits" form pre-fills from.
  submission: QcSubmissionAttributes;
};

export type QcSubmissionAttributes = {
  // Every risk the submission carries. The approve-with-edits form edits risks[0] - QC corrects one
  // call - but the reviewer sees the whole set.
  risks: string[];
  notes: string | null;
  example_ids: string | null;
  submission_type: string;
  language: string[];
  topic: string[];
  location: string[];
  core_risk: string[];
};

export type QcSubmissionType = 'Content' | 'Actor';

export type QcReviewQuery = {
  policy_violation?: string;
  status?: QcReviewStatus;
  analyst?: string;
  submission_type?: QcSubmissionType;
  page?: number;
  page_size?: number;
};

/**
 Omitted fields = approve as-is. `notes` is the reviewer's own note on the review; every other field
 corrects an attribute the analyst supplied (`submission_notes` is the client-facing note), and a
 value equal to the current one is not counted as an edit. Each `enrichment` list replaces the
 submission's current one wholesale. clear_qc_for_analyst clears the item's analyst's
 QcRequiredVerticals to [] (QC on no vertical) in the same request; clear_qc_for_vertical removes only
 this item's (possibly edited) vertical from that set. Both may be set - clear_qc_for_analyst wins.
 */
export type ApproveQcReviewPayload = {
  policy_violation?: string;
  risk?: string;
  notes?: string;
  submission_notes?: string;
  example_ids?: string;
  enrichment?: {
    language?: string[];
    topic?: string[];
    location?: string[];
    core_risk?: string[];
  };
  clear_qc_for_analyst?: boolean;
  clear_qc_for_vertical?: boolean;
};

export type RejectQcReviewPayload = {
  notes: string;
};

export type BulkApproveQcPayload = {
  analyst_email: string;
  // Omitted = every pending item for this analyst; set = only their pending items in this vertical.
  policy_violation?: string;
  clear_qc_for_analyst?: boolean;
  clear_qc_for_vertical?: boolean;
};

export type BulkApproveQcResponse = {
  approved_count: number;
};

export type QcAnalystSummary = {
  id: string;
  email: string;
  display_name: string;
  requires_qc_review: boolean;
  // Per-vertical QC gate: null = every vertical requires QC, [] = none, a subset = only those.
  qc_required_verticals: string[] | null;
  pending_count: number;
  // Blends Weekly QA's sampled true/false-positive reviews with this analyst's own QC approve/reject
  // outcomes into one true/false-positive rate - a QC approval counts as a true positive, a QC
  // rejection as a false positive. accuracy_rate is null until there's at least one reviewed item
  // from either source (never NaN - always check for null, not falsiness).
  total_reviewed: number;
  true_positive_count: number;
  false_positive_count: number;
  accuracy_rate: number | null;
};

// QC decisions per vertical or analyst. Only approved-as-is counts as accurate.
export type QcAccuracy = {
  total_reviewed: number;
  approved: number;
  approved_with_edits: number;
  rejected: number;
  accuracy_rate: number;
};
export type QcVerticalAccuracy = QcAccuracy & {policy_violation: string};
export type QcAnalystAccuracy = QcAccuracy & {analyst_email: string};
export type QcAccuracyQuery = {from?: string; to?: string; policy_violation?: string};

export const qcReviewApi = {
  verticalAccuracy: async (parameters?: Pick<QcAccuracyQuery, 'from' | 'to'>) =>
    get<QcVerticalAccuracy[]>('/qc-review/accuracy/verticals', parameters),

  // Compares analysts, so the API refuses it without read:reports:leads.
  accuracy: async (parameters?: QcAccuracyQuery) =>
    get<QcAnalystAccuracy[]>('/qc-review/accuracy', parameters),

  policyViolations: async (allVerticals = false) =>
    get<QcPolicyViolationQueueSummary[]>('/qc-review/policy-violations', {
      all_verticals: allVerticals || undefined
    }),

  list: async (parameters?: QcReviewQuery) => get<QcReviewItem[]>('/qc-review', parameters),

  get: async (id: string) => get<QcReviewItem>(`/qc-review/${id}`),

  approve: async (id: string, payload: ApproveQcReviewPayload) =>
    patch<ApproveQcReviewPayload, QcReviewItem>(`/qc-review/${id}/approve`, payload),

  reject: async (id: string, payload: RejectQcReviewPayload) =>
    patch<RejectQcReviewPayload, QcReviewItem>(`/qc-review/${id}/reject`, payload),

  bulkApprove: async (payload: BulkApproveQcPayload) =>
    post<BulkApproveQcPayload, BulkApproveQcResponse>('/qc-review/bulk-approve', payload),

  analysts: async (q?: string) =>
    get<QcAnalystSummary[]>('/qc-review/analysts', q ? {q} : undefined)
};
